import { AloftPrice, orUndefined, schAloftPrice } from '@aloft/toolkit-shared';
import { Block, Flex, SimpleInput, Typography } from '@aloft/toolkit-sui';

import { useEffect } from 'react';
import { Control, Controller, Form, FormProvider, SubmitHandler } from 'react-hook-form';

import { FormTextarea } from '../../../components/FormTextarea/FormTextarea.tsx';
import { useAppraisifyValidation } from '../../../hooks/validation/useAppraisifyValidation.ts';
import { submitReconciliationDataSheet } from '../../../redux/features/reconciliationSlice.ts';
import { selectCommentary } from '../../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectIsArvOrder } from '../../../redux/features/selectors/orderSelectors.ts';
import { selectReconciliation } from '../../../redux/features/selectors/selectReconciliation.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { useReconciliationSheetForm } from './hooks/useReconciliationSheetForm.ts';
import { ReconciliationSheetFormOutput } from './schemas.ts';

type ReconciliationFieldProps = {
  control: Control;
  fieldName: keyof ReconciliationSheetFormOutput;
  label: string;
  handleChange: () => void;
};

const ReconciliationField = ({
  control,
  fieldName,
  label,
  handleChange,
}: ReconciliationFieldProps) => {
  const validation = useAppraisifyValidation();
  const error = validation.errors.Reconciliation[fieldName];

  return (
    <Flex flexDirection="column" flex={1}>
      <Block marginBottom="5px">
        <Typography
          variant="h3"
          color="textSecondary"
          __dangerDoNotUseSx={{ whiteSpace: 'nowrap' }}
        >
          {label}
          <span className="simple-field-required" style={{ marginLeft: '2px' }}>
            *
          </span>
        </Typography>
      </Block>
      <Controller
        name={fieldName}
        control={control}
        render={({ field, fieldState }) => (
          <SimpleInput
            type="number"
            value={field.value ? field.value.toString(true, true) : ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value ? AloftPrice.fromDollarLike(e.target.value) : undefined;
              field.onChange(value);
              handleChange();
            }}
            onBlur={field.onBlur}
            precision={0}
            prefix="$ "
            useSeparator
            required
            placeholder="Required"
            errorMessage={fieldState.isTouched ? error?.message : undefined}
            invalid={fieldState.invalid && fieldState.isTouched}
          />
        )}
      />
    </Flex>
  );
};

export const ReconciliationSheet = () => {
  const dispatch = useAppDispatch();
  const form = useReconciliationSheetForm();
  const validation = useAppraisifyValidation();
  const { reconciliationCommentary: reconciliationCommentaryError } =
    validation.errors.Reconciliation;

  const { reconciliation: reconciliationCommentary } = useAppSelector(selectCommentary);
  const reconciliation = useAppSelector(selectReconciliation);
  const hasArvOption = useAppSelector(selectIsArvOrder);

  useEffect(() => {
    form.reset({
      reconciliationCommentary: reconciliationCommentary?.reconciliationCommentary ?? '',
      totalCostToCure: orUndefined(schAloftPrice.parse)(reconciliation.totalCostToCure),
      totalRenovationBudget: orUndefined(schAloftPrice.parse)(reconciliation.totalRenovationBudget),
      opinionOfMarketValue: orUndefined(schAloftPrice.parse)(reconciliation.opinionOfMarketValue),
      opinionOfAfterRepairValue: orUndefined(schAloftPrice.parse)(
        reconciliation.opinionOfAfterRepairValue,
      ),
    });
  }, []);

  const onSubmit: SubmitHandler<ReconciliationSheetFormOutput> = (data) => {
    dispatch(submitReconciliationDataSheet(data));
  };

  const handleChange = () => {
    form
      .handleSubmit(onSubmit, undefined)()
      .catch(() => {});
  };

  return (
    <Flex flexDirection="column" marginLeft="2rem" marginRight="4rem" height="100%">
      <Typography variant="h1">Reconciliation</Typography>
      <FormProvider {...form}>
        <Form onChange={handleChange} style={{ height: '100%' }}>
          <Flex marginTop="20px" gap={4} flexWrap="wrap">
            {hasArvOption && (
              <>
                <ReconciliationField
                  control={form.control}
                  fieldName="totalRenovationBudget"
                  label="Total Renovation Budget"
                  handleChange={handleChange}
                />
                <ReconciliationField
                  control={form.control}
                  fieldName="opinionOfMarketValue"
                  label="Opinion of As Is Value"
                  handleChange={handleChange}
                />
                <ReconciliationField
                  control={form.control}
                  fieldName="opinionOfAfterRepairValue"
                  label="Opinion of After Repair Value"
                  handleChange={handleChange}
                />
              </>
            )}
            {!hasArvOption && (
              <>
                {reconciliation?.includeTotalCostToCure && (
                  <ReconciliationField
                    control={form.control}
                    fieldName="totalCostToCure"
                    label="Total Cost to Cure"
                    handleChange={handleChange}
                  />
                )}
                <ReconciliationField
                  control={form.control}
                  fieldName="opinionOfMarketValue"
                  label="Opinion of Market Value"
                  handleChange={handleChange}
                />
              </>
            )}
          </Flex>
          <Flex marginTop="20px" flexDirection="column" flex={1} height="80%">
            <Controller
              name="reconciliationCommentary"
              control={form.control}
              render={({ fieldState }) => (
                <FormTextarea
                  name="reconciliationCommentary"
                  control={form.control}
                  label="Reconciliation Commentary"
                  placeholder="Required"
                  isRequired
                  column
                  resize="vertical"
                  customErrorMessage={
                    fieldState.isTouched ? reconciliationCommentaryError?.message : undefined
                  }
                />
              )}
            />
          </Flex>
        </Form>
      </FormProvider>
    </Flex>
  );
};
